import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import API_URL from '../config';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 380px;
  background: #ffffff;
  border-radius: 16px;
  padding: 2.5rem 2rem;
  box-shadow: 0 24px 48px rgba(0,0,0,0.4);
  animation: ${fadeIn} 0.3s ease;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const LogoMark = styled.div`
  width: 42px;
  height: 42px;
  background: #4f46e5;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -1px;
`;

const LogoText = styled.div`
  h1 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }
  p {
    margin: 2px 0 0;
    font-size: 12px;
    color: #94a3b8;
  }
`;

const Heading = styled.h2`
  margin: 0 0 0.375rem;
  font-size: 1.375rem;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
  letter-spacing: -0.02em;
`;

const Sub = styled.p`
  margin: 0 0 1.75rem;
  font-size: 13.5px;
  color: #64748b;
  text-align: center;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.5rem;
`;

const PasswordWrap = styled.div`
  position: relative;
  margin-bottom: 1.25rem;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 0.7rem 2.5rem 0.7rem 0.9rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  background: #f8fafc;
  color: #0f172a;
  -webkit-text-fill-color: #0f172a;
  font-size: 14px;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;

  &::placeholder { color: #94a3b8; }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: #0f172a;
    -webkit-box-shadow: 0 0 0px 1000px #f8fafc inset;
    transition: background-color 5000s ease-in-out 0s;
  }

  &:focus {
    outline: none;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79,70,229,0.12);
    background: #fff;
    &:-webkit-autofill { -webkit-box-shadow: 0 0 0px 1000px #fff inset; }
  }
  &:disabled { opacity: 0.6; }
`;

const EyeBtn = styled.button`
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  transition: color 0.15s;
  &:hover { color: #475569; }
`;

const ErrorMsg = styled.p`
  margin: -0.75rem 0 1rem;
  font-size: 13px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const LoginBtn = styled.button`
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-radius: 9px;
  background: ${p => p.disabled ? '#e2e8f0' : '#4f46e5'};
  color: ${p => p.disabled ? '#94a3b8' : '#fff'};
  font-size: 14.5px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.15s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #4338ca;
    transform: translateY(-1px);
  }
`;

function AuthScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const inputRef = useRef(null);

  // Читаем реальное значение из DOM — браузерное автозаполнение
  // не всегда триггерит React onChange, поэтому берём из ref напрямую
  const getValue = () => inputRef.current?.value || password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pwd = getValue();
    if (!pwd.trim()) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(
        `${API_URL}/api/audio/check-access?password=${encodeURIComponent(pwd)}&section=ss`
      );
      const data = await res.json();

      if (data.isValid) {
        onLogin(pwd);
      } else {
        setError(data.message || 'Неверный пароль');
      }
    } catch {
      setError('Не удалось подключиться к серверу');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Card>
        <LogoRow>
          <LogoMark>НБ</LogoMark>
          <LogoText>
            <h1>Наедине с Богом</h1>
            <p>Admin Panel</p>
          </LogoText>
        </LogoRow>

        <Heading>Вход</Heading>
        <Sub>Введите пароль администратора</Sub>

        <form onSubmit={handleSubmit}>
          <FieldLabel htmlFor="login-pwd">Пароль</FieldLabel>
          <PasswordWrap>
            <PasswordInput
              ref={inputRef}
              id="login-pwd"
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder="Введите пароль"
              autoComplete="current-password"
              disabled={loading}
              autoFocus
            />
            <EyeBtn
              type="button"
              onClick={() => setShowPwd(v => !v)}
              tabIndex={-1}
              title={showPwd ? 'Скрыть' : 'Показать'}
            >
              {showPwd ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </EyeBtn>
          </PasswordWrap>

          {error && <ErrorMsg>⚠ {error}</ErrorMsg>}

          <LoginBtn type="submit" disabled={loading || !password.trim()}>
            {loading ? (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Проверка…
              </>
            ) : 'Войти'}
          </LoginBtn>
        </form>
      </Card>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Page>
  );
}

export default AuthScreen;
